import {getLocalizedStrings} from '@/localization/localization-store';
import {getProductSelection, selectPlan} from '@/product/product-selection';

/**
 * Renders a list of delivery options as a frequency helper selector.
 * The title of each button is a mapped value from the `frequency_helper_options` setting.
 * This allows us to customize the text of each button, while mapping onto the delivery options.
 * The title of the form is a mapped value from the `frequency_helper_title` setting.
 */

export default function FrequencyHelper({
  showOnetimeOption = false,
  onSelectOnetime,
}: {
  showOnetimeOption?: boolean;
  onSelectOnetime?: () => void;
}) {
  const {monthlyPlans, selectedPlan} = getProductSelection();

  const strings = getLocalizedStrings();
  const options = [
    {
      title: strings.buyStackFrequencyTitleFirst ?? '3+ People',
      description: strings.buyStackFrequencyDescriptionFirst ?? 'Every 1 Month',
    },
    {
      title: strings.buyStackFrequencyTitleSecond ?? '2 People',
      description:
        strings.buyStackFrequencyDescriptionSecond ?? 'Every 2 Months',
    },
    {
      title: strings.buyStackFrequencyTitleThird ?? '1 Person',
      description:
        strings.buyStackFrequencyDescriptionThird ?? 'Every 3-4 Months',
    },
  ];

  // Slice the options to match the number of monthly plans available.
  const optionsSliced = options.slice(0, monthlyPlans.length);
  const headerText =
    strings.buyStackFrequencyHelperHeader ?? 'How Large is Your Household?';

  return (
    <form
      className="flex flex-col items-center gap-y-4 text-center"
      onSubmit={(e) => e.preventDefault()}
    >
      <h6 className="text-base font-semibold text-neutral-dark-gray">
        {headerText}
      </h6>
      <div
        className={`grid w-full max-w-52 grid-cols-1 gap-3 md:max-w-none ${showOnetimeOption ? 'lg:grid-cols-4' : 'md:grid-cols-3'}`}
      >
        {optionsSliced.map((option, i) => {
          const {title, description} = option;
          const plan = monthlyPlans[i];
          const isSelected = plan === selectedPlan;

          return (
            <FrequencyHelperOptionButton
              onClick={() => selectPlan(plan)}
              key={title}
              title={title}
              description={description}
              isSelected={isSelected}
            ></FrequencyHelperOptionButton>
          );
        })}

        {showOnetimeOption && (
          <FrequencyHelperOptionButton
            onClick={onSelectOnetime ?? (() => selectPlan(null))}
            title={'1 Time Purchase Option'}
            description={'Ships One Time'}
            isSelected={selectedPlan == null}
          ></FrequencyHelperOptionButton>
        )}
      </div>
    </form>
  );
}

function FrequencyHelperOptionButton({
  onClick,
  title,
  description,
  isSelected,
}: {
  onClick?: () => void;
  title: string;
  description: string;
  isSelected: boolean;
}) {
  return (
    <button
      className="relative z-10 flex w-full flex-col items-center rounded border-2 border-primary-blue px-2 py-1.5 text-sm text-neutral-dark-gray"
      onClick={onClick}
    >
      {isSelected && (
        <div className="absolute inset-0 -z-10 bg-primary-blue/20"></div>
      )}
      <span>{title}</span>
      <span className="text-xs font-normal">{description}</span>
    </button>
  );
}
